import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ProtectedView, Unauthorized } from '@register-ui/components';
import { FEATURE_FLAGS, PERMISSION_CODES } from '@register-ui/utils';
import { Outlet, type RouteObject } from 'react-router-dom';

const saleRoutes: RouteObject[] = [
    {
        path: '',
        children: [
            {
                path: 'sell',
                element: <Outlet />,
                children: [
                    {
                        path: '',
                        async lazy() {
                            const SellPage = await import('../features/sell');

                            return {
                                Component: SellPage.default,
                            };
                        },
                    },
                    {
                        path: 'checkout',
                        async lazy() {
                            const CheckoutPage =
                                await import('../features/checkout');

                            return { Component: CheckoutPage.default };
                        },
                    },
                ],
            },
            {
                path: 'sales',
                element: <Outlet />,
                children: [
                    {
                        path: '',
                        children: [
                            {
                                path: 'completed',
                                element: (
                                    <ProtectedView
                                        altView={<Unauthorized />}
                                        accessList={[
                                            PERMISSION_CODES.VIEW_SALES,
                                            PERMISSION_CODES.VIEW_COMPLETED_SALE,
                                            PERMISSION_CODES.VIEW_OWN_COMPLETED_SALE,
                                        ]}
                                        grantAccessIfAny>
                                        <Outlet />
                                    </ProtectedView>
                                ),
                                children: [
                                    {
                                        path: '',
                                        async lazy() {
                                            const CompletedSales =
                                                await import('../features/completed-sales');

                                            return {
                                                Component:
                                                    CompletedSales.default,
                                            };
                                        },
                                    },
                                    {
                                        path: ':saleId/refund',
                                        element: (
                                            <FeatureProtectedView
                                                flag={
                                                    FEATURE_FLAGS.ENABLE_REFUNDS
                                                }>
                                                <ProtectedView
                                                    altView={<Unauthorized />}
                                                    accessList={[
                                                        PERMISSION_CODES.REFUND_SALE,
                                                        PERMISSION_CODES.VIEW_SALES,
                                                        PERMISSION_CODES.VIEW_COMPLETED_SALE,
                                                        PERMISSION_CODES.VIEW_OWN_COMPLETED_SALE,
                                                    ]}
                                                    grantAccessIfAny>
                                                    <Outlet />
                                                </ProtectedView>
                                            </FeatureProtectedView>
                                        ),
                                        children: [
                                            {
                                                path: '',
                                                async lazy() {
                                                    const RefundSale =
                                                        await import('../features/refunds/refund-sale');
                                                    return {
                                                        Component:
                                                            RefundSale.default,
                                                    };
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'outstanding',
                                element: (
                                    <ProtectedView
                                        altView={<Unauthorized />}
                                        accessList={[
                                            PERMISSION_CODES.VIEW_SALES,
                                            PERMISSION_CODES.VIEW_OUTSTANDING_SALE,
                                            PERMISSION_CODES.VIEW_OWN_OUTSTANDING_SALE,
                                        ]}
                                        grantAccessIfAny>
                                        <Outlet />
                                    </ProtectedView>
                                ),
                                children: [
                                    {
                                        path: '',
                                        async lazy() {
                                            const OutstandingSales =
                                                await import('../features/outstanding-sales');

                                            return {
                                                Component:
                                                    OutstandingSales.default,
                                            };
                                        },
                                    },
                                    {
                                        path: ':saleId/refund',
                                        element: (
                                            <FeatureProtectedView
                                                flag={
                                                    FEATURE_FLAGS.ENABLE_REFUNDS
                                                }>
                                                <ProtectedView
                                                    accessList={[
                                                        PERMISSION_CODES.REFUND_SALE,
                                                    ]}>
                                                    <Outlet />
                                                </ProtectedView>
                                            </FeatureProtectedView>
                                        ),
                                        children: [
                                            {
                                                path: '',
                                                async lazy() {
                                                    const RefundSale =
                                                        await import('../features/refunds/refund-sale');

                                                    return {
                                                        Component:
                                                            RefundSale.default,
                                                    };
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'refunds',
                                element: (
                                    <FeatureProtectedView
                                        flag={FEATURE_FLAGS.ENABLE_REFUNDS}>
                                        <ProtectedView
                                            altView={<Unauthorized />}
                                            accessList={[
                                                PERMISSION_CODES.VIEW_SALES,
                                                PERMISSION_CODES.VIEW_COMPLETED_SALE,
                                                PERMISSION_CODES.VIEW_OWN_COMPLETED_SALE,
                                            ]}
                                            grantAccessIfAny>
                                            <Outlet />
                                        </ProtectedView>
                                    </FeatureProtectedView>
                                ),
                                children: [
                                    {
                                        path: '',
                                        async lazy() {
                                            const RefundsPage =
                                                await import('../features/refunds/refunds-page');

                                            return {
                                                Component: RefundsPage.default,
                                            };
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'unsynced',
                        async lazy() {
                            const UnsyncedSales =
                                await import('../features/unsynced-sales');

                            return { Component: UnsyncedSales.default };
                        },
                    },
                ],
            },
            {
                path: 'saved-carts',
                element: <Outlet />,
                children: [
                    {
                        path: '',
                        async lazy() {
                            const Page =
                                await import('../features/remote-carts');

                            return { Component: Page.RemoteCartPage };
                        },
                    },
                    {
                        path: 'others',
                        async lazy() {
                            const Page =
                                await import('../features/remote-carts/list');

                            return { Component: Page.RemoteCartList };
                        },
                    },

                    // TODO: Remove after migration
                    {
                        path: 'unsynced',
                        async lazy() {
                            const Page =
                                await import('../features/saved-carts/unsynced-saved-carts');

                            return { Component: Page.default };
                        },
                    },
                ],
            },
            {
                path: 'wallet',
                element: <Outlet />,
                children: [
                    {
                        path: 'topup',
                        element: (
                            <ProtectedView
                                altView={<Unauthorized />}
                                accessList={[PERMISSION_CODES.FUND_WALLET]}
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const WalletTopUp =
                                        await import('../features/wallet-topup');

                                    return {
                                        Component: WalletTopUp.default,
                                    };
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

// eslint-disable-next-line import/no-default-export
export default saleRoutes;
